/**
 * bank-accounts-columns.tsx — budowa `ColumnDefinition[]` dla `ConfigurableTable`
 * z dynamicznych pól helper-encji `contractor_bank_accounts`/`company_bank_accounts`.
 *
 * Wzorzec z `ModuleRecordsPage.tsx` (kolumny z pól modułu custom), kategorie filtrów
 * przez `mapExtFieldTypeToFilterCategory` (`lib/ext-list-columns.tsx`) — słownictwo
 * typów pól SYSTEMOWYCH (`varchar`/`text`/`boolean`/…), nie custom-modułowych.
 *
 * Kolumny budowane z WSZYSTKICH pól layoutu (nie tylko `showInList`) —
 * `defaultVisible: field.showInList` odtwarza dzisiejszy domyślny widok, ale
 * sesyjny column-chooser `ConfigurableTable` pozwala odkryć pola domyślnie skryte
 * (np. `swift_bic`).
 *
 * Pułapka (patrz plan refactoru): `accessor` MUSI zwracać surową wartość (nigdy
 * wyrenderowany glif/etykietę) — `ConfigurableTable` filtruje kolumny `boolean`
 * po `String(accessor(row))` porównywanym z `'true'/'false'`, całkowicie niezależnie
 * od tego, co pokazuje `renderCell`.
 *
 * Ext-pola (`field.isCore === false`) są renderowane (parytet z dawnym kodem, patrz
 * `renderFieldCellContent`), ale ŚWIADOMIE bez filtra/sortowania: `GET .../bank-accounts`
 * obu endpointów robi płaski `.select()` z tabeli core i NIE dołącza wartości z tabeli
 * `*_ext` (`apps/api/app/api/contractors/[contractorId]/bank-accounts/route.ts`,
 * `apps/api/app/api/companies/[companyId]/bank-accounts/route.ts`) — kolumna ext w tej
 * liście jest więc dziś zawsze pusta w produkcji (dług sprzed tego refaktoru). Filtr na
 * takiej kolumnie porównywałby wyłącznie `undefined` i CICHO czyściłby całą listę —
 * gorsze niż dawny brak filtrowania w ogóle. Backlog: dołożyć odczyt ext do obu tras GET.
 */

import type { ColumnDefinition } from '@/components/configurable-table';
import type {
  ColumnFilterCategory,
  ColumnHeaderFilterConfig,
} from '@/components/configurable-table/types';
import { formatDictionaryOptionLabel, useDictionary } from '@/hooks/useDictionary';
import {
  mapExtFieldTypeToFilterCategory,
  renderBooleanCell,
  renderExtFieldValue,
} from '@/lib/ext-list-columns';
import { formatIban } from '@/lib/iban';
import { slugToCamel } from '@/lib/slug-to-camel';

import { type WhitelistStatus, WhitelistStatusBadge } from './WhitelistStatusBadge';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export interface SystemModuleField {
  id: string;
  slug: string;
  name: string;
  fieldType: string;
  isCore: boolean;
  isRequired: boolean;
  description: string | null;
  showInList: boolean;
  /** Kolejność w katalogu pól — wymagane przez `renderExtFieldValue` (kontrakt
   *  `SystemModuleFieldDef`), nieużywane lokalnie (kolejność kolumn tu bierze się
   *  z DFS layoutu, nie z tego pola). */
  sortOrder: number;
  /** Flaga admina „pole filtrowalne" — steruje filtrowaniem SERWEROWYM, którego ta
   *  zakładka nie ma (filtrujemy lokalnie po już pobranych rekordach). Nieużywana do
   *  gatowania `headerFilter` tutaj, wymagana tylko dla zgodności typu z `renderExtFieldValue`. */
  isFilterable: boolean;
  options: Array<{ value: string; label: string; color?: string }> | null;
  dictionaryCode: string | null;
  dictionaryDisplayMode?: 'label' | 'code' | 'code_label' | null;
  gcrecord: string | null;
}

export interface FieldsResponse {
  fields: SystemModuleField[];
}

/**
 * Wiersz konta bankowego — pola core wspólne dla kontrahenta i firmy, plus
 * `notes`/`purpose` (różnica nazwy pola między encjami) i pola whitelist
 * (tylko kontrahent) jako opcjonalne. BRAK index signature (świadomie, patrz
 * `readField` niżej) — dynamiczny dostęp po camelCase slugu idzie przez JEDEN
 * lokalny, świadomy cast, nie przez dziurę w typie całego wiersza.
 */
export interface BankAccountRow {
  id: string;
  accountNumber: string;
  swiftBic: string | null;
  bankName: string | null;
  currencyCode: string;
  isPrimary: boolean;
  isActive: boolean;
  notes?: string | null;
  purpose?: string | null;
  contractorId?: string;
  companyId?: string;
  whitelistStatus?: WhitelistStatus | null;
  whitelistCheckedAt?: string | null;
}

/** Odczyt dynamicznego pola (core lub ext) po camelCase slugu — jeden świadomy cast
 *  w jednym miejscu, zamiast index signature na całym `BankAccountRow`. Eksportowane —
 *  `BankAccountsTabBase.tsx` potrzebuje tego samego dostępu przy budowie formularza
 *  (`accountRowToFormValues`). */
export function readField(row: BankAccountRow, camelKey: string): unknown {
  return (row as unknown as Record<string, unknown>)[camelKey];
}

const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  account_number: 260,
  bank_name: 180,
  swift_bic: 120,
  // 120 (nie 90) — kontrolka filtra wiersza (dropdown Wszystkie/Tak/Nie, popup
  // wyboru waluty) potrzebuje miejsca na etykietę, nie tylko na treść komórki.
  currency_code: 120,
  is_primary: 120,
  is_active: 120,
  notes: 220,
  purpose: 180,
};
const DEFAULT_EXT_COLUMN_WIDTH = 140;
const WHITELIST_COLUMN_WIDTH = 150;

/** Mapa `WhitelistStatus` → segment klucza i18n (`tenant.contractors.bankAccounts.whitelist.*`).
 *  `Record` (nie tablica) — dodanie stanu do unii `WhitelistStatus` bez wpisu tutaj jest
 *  błędem kompilacji, nie cichym pominięciem w liście opcji filtra. */
const WHITELIST_STATUS_I18N_KEY: Record<WhitelistStatus, string> = {
  active: 'active',
  not_found: 'notFound',
  virtual_unverified: 'virtualUnverified',
  not_applicable: 'notApplicable',
};

/**
 * Renderuje wartość ext-pola `select`/`multiselect` powiązanego ze słownikiem
 * (`field.dictionaryCode`) w komórce tabeli (read-only listing). Wyodrębnione
 * do osobnego komponentu, żeby `useDictionary` był wywoływany bezwarunkowo na
 * jego własnym poziomie (rules-of-hooks).
 */
function DictionaryCellValue({
  dictionaryCode,
  dictionaryDisplayMode,
  code,
}: {
  dictionaryCode: string;
  dictionaryDisplayMode: 'label' | 'code' | 'code_label' | null | undefined;
  code: string;
}): React.ReactNode {
  const { dictionary, itemsByCode } = useDictionary(dictionaryCode);
  if (!dictionary) return code;
  const item = itemsByCode.get(code);
  if (!item) return code;
  return formatDictionaryOptionLabel(item, dictionaryDisplayMode ?? dictionary.displayMode);
}

function fieldFilterCategory(field: SystemModuleField): ColumnFilterCategory {
  // `currency_code` jest `varchar` (brak konceptu select/dictionary w schemacie core) —
  // override do `select`, opcje wyliczone z załadowanych wierszy (patrz `fieldFilterOptions`).
  if (field.slug === 'currency_code') return 'select';
  return mapExtFieldTypeToFilterCategory(field.fieldType);
}

function distinctCurrencyOptions(
  accounts: BankAccountRow[],
): Array<{ value: string; label: string }> {
  const codes = new Set<string>();
  for (const account of accounts) {
    if (typeof account.currencyCode === 'string' && account.currencyCode) {
      codes.add(account.currencyCode);
    }
  }
  return Array.from(codes)
    .sort()
    .map((code) => ({ value: code, label: code }));
}

function fieldFilterOptions(
  field: SystemModuleField,
  accounts: BankAccountRow[],
): Array<{ value: string; label: string; color?: string }> | undefined {
  if (field.slug === 'currency_code') return distinctCurrencyOptions(accounts);
  // Ext-pole `select` z opcjami inline (nie ze słownikiem) — mirror `buildExtColumns`.
  if (field.fieldType === 'select' && !field.dictionaryCode && field.options) {
    return field.options;
  }
  return undefined;
}

function renderFieldCellContent(
  field: SystemModuleField,
  value: unknown,
  row: BankAccountRow,
  t: TranslateFn,
): React.ReactNode {
  if (field.fieldType === 'boolean') {
    // Świadoma zmiana wizualna vs dawny render `'✓'`/`''`: wyłączony `Checkbox`,
    // wspólny z resztą listingów w aplikacji (`renderBooleanCell`, ext-list-columns.tsx)
    // — realizuje cel „ujednolić wygląd" z brainstormu tego refactoru.
    return renderBooleanCell(value, t);
  }

  // Ext-pole ze słownikiem (dowolny dictionaryCode dodany przez tenanta przez builder
  // pól) — core pola tej helper-encji nigdy nie mają dictionaryCode, więc ten branch
  // dotyczy WYŁĄCZNIE ext; zostaje osobno (nie przez `renderExtFieldValue` niżej), bo
  // ten renderer nie ma odpowiednika `DictionaryCellValue`.
  if (field.dictionaryCode) {
    if (value === null || value === undefined) return null;
    return (
      <DictionaryCellValue
        dictionaryCode={field.dictionaryCode}
        dictionaryDisplayMode={field.dictionaryDisplayMode}
        code={String(value)}
      />
    );
  }

  // Każde inne ext-pole (bez słownika) — deleguj do wspólnego renderera typów pól
  // systemowych (data/timestamp/select/multiselect/reference/…), ten sam co na
  // listach kartotek (`buildExtColumns`). Usuwa trzecią kopię tej logiki i dodaje
  // za darmo formatowanie dat/multiselect, którego dawny kod tu nie miał.
  if (!field.isCore) {
    return renderExtFieldValue(value, field, t);
  }

  if (value === null || value === undefined) return null;
  const text = String(value);

  // IBAN formatting — `formatIban` zmienia canonical bez spacji na grupy po 4
  // (PL12345678901234567890 → PL12 3456 7890 1234 5678 9012).
  if (field.slug === 'account_number') {
    const formatted = formatIban(text);
    if (row.isPrimary) {
      return (
        <span className="inline-flex items-center gap-1">
          <span className="font-mono text-xs">{formatted}</span>
          <span className="inline-flex items-center rounded bg-accent-100 px-1.5 py-0.5 text-xs text-accent-700">
            ★
          </span>
        </span>
      );
    }
    return <span className="font-mono text-xs">{formatted}</span>;
  }

  return text;
}

function buildFieldColumn(
  field: SystemModuleField,
  accounts: BankAccountRow[],
  testIdPrefix: string,
  t: TranslateFn,
): ColumnDefinition<BankAccountRow> {
  const camelKey = slugToCamel(field.slug);
  // Ext-pola: bez filtra/sortowania — patrz komentarz na górze pliku (GET nie zwraca
  // dziś wartości ext, filtr/sort na `undefined` byłby cichym bugiem, nie funkcją).
  const isExt = !field.isCore;
  const category: ColumnFilterCategory = isExt ? 'none' : fieldFilterCategory(field);
  const options = isExt ? undefined : fieldFilterOptions(field, accounts);
  const hasOptions = (options?.length ?? 0) > 0;

  const headerFilter: ColumnHeaderFilterConfig | undefined =
    category === 'none'
      ? undefined
      : {
          category,
          fieldSlug: field.slug,
          fieldType: field.fieldType,
          ...(options ? { options } : {}),
        };

  // `ColumnDefinition.filterable` (domyślnie włączone — wyłącza je tylko jawne `false`,
  // `types.ts`) jest czytane w JEDNYM miejscu żywego dyspozytora wiersza filtrów —
  // `filter-row/FilterRowCell.tsx` (NIE `filter-row-controls.tsx`, który tylko dostarcza
  // kontrolki) — i TYLKO w jego ostatniej gałęzi, polu „Szukaj…". Kategorie z własną kontrolką
  // (`boolean`, `date`, `number`, `text` oraz `select` / `single-choice` / `multi-choice` /
  // `record-select` Z OPCJAMI) dostają ją w wierszu filtrów NIEZALEŻNIE od tej flagi (dispatch
  // po samej `category`, wcześniej niż ta gałąź). Jedyny przypadek w tym pliku, w którym flaga
  // coś włącza, to pole PODSTAWOWE typu `select` bez wyliczonych opcji
  // (`!isExt && category === 'select' && !hasOptions` — dziś `currency_code` przy liście kont
  // bez żadnej waluty; pola podstawowe nie mają słownika, patrz komentarz przy
  // `field.dictionaryCode` w `renderFieldCellContent`): dostaje wtedy pole „Szukaj…" zamiast
  // pustej komórki. Pola dodatkowe nie mają `headerFilter` (`category: 'none'`), więc też kończą
  // w tej gałęzi — jawne `false` wyłącza dla nich pole „Szukaj…" (filtr na wartościach ext,
  // których lista nie dostaje, byłby cichym bugiem, patrz nagłówek pliku).
  const filterable = !isExt && category === 'select' && !hasOptions;

  return {
    id: field.slug,
    // Surowa wartość, NIGDY wyrenderowany glif/etykieta — inaczej filtr boolean
    // (i każdy inny filtr porównujący `accessor(row)`) nic nie znajdzie.
    accessor: (row) => readField(row, camelKey),
    header: field.name,
    defaultVisible: field.showInList,
    defaultWidth: DEFAULT_COLUMN_WIDTHS[field.slug] ?? DEFAULT_EXT_COLUMN_WIDTH,
    // Sortowanie po gwiazdce (konto główne) bez sensu; ext-pola bez sortu (patrz wyżej).
    sortable: !isExt && field.slug !== 'is_primary',
    filterable,
    ...(headerFilter ? { headerFilter } : {}),
    renderCell: (value, row) => (
      <span data-testid={`${testIdPrefix}-row-${row.id}-${field.slug}`}>
        {renderFieldCellContent(field, value, row, t)}
      </span>
    ),
  };
}

function buildWhitelistColumn(
  testIdPrefix: string,
  t: TranslateFn,
): ColumnDefinition<BankAccountRow> {
  const options = (Object.keys(WHITELIST_STATUS_I18N_KEY) as WhitelistStatus[]).map((status) => ({
    value: status,
    label: t(`tenant.contractors.bankAccounts.whitelist.${WHITELIST_STATUS_I18N_KEY[status]}`),
  }));

  return {
    id: 'whitelist_status',
    accessor: (row) => row.whitelistStatus ?? null,
    header: t('tenant.contractors.bankAccounts.columns.whitelist'),
    defaultVisible: true,
    defaultWidth: WHITELIST_COLUMN_WIDTH,
    sortable: false,
    // `hideable: false` chroni tylko modal „Konfiguracja kolumn" (`ColumnConfigModal`) —
    // menu kontekstowe nagłówka „Ukryj" tę flagę ignoruje i schowa kolumnę mimo to
    // (stan sesyjny, `persistConfig={false}` — wraca po przeładowaniu). Naprawa
    // właściwa leży w `ConfigurableTable` (poza zakresem tego refactoru).
    hideable: false,
    headerFilter: { category: 'select', fieldSlug: 'whitelist_status', options },
    renderCell: (value, row) => (
      <span data-testid={`${testIdPrefix}-row-${row.id}-whitelist_status`}>
        <WhitelistStatusBadge status={isWhitelistStatus(value) ? value : null} />
      </span>
    ),
  };
}

/** Zawężenie typu bez ślepego castu — nieznana wartość z backendu (np. przyszły stan
 *  poza dzisiejszą unią) renderuje się jako `null` („Niesprawdzone"), nie ląduje w
 *  badge'u pod fałszywą etykietą. */
function isWhitelistStatus(value: unknown): value is WhitelistStatus {
  return typeof value === 'string' && value in WHITELIST_STATUS_I18N_KEY;
}

export interface BuildBankAccountColumnsArgs {
  /** Wszystkie pola layoutu formularza (nie tylko `showInList`) — patrz nagłówek pliku. */
  listColumns: SystemModuleField[];
  accounts: BankAccountRow[];
  /** `true` = dołóż stałą kolumnę statusu białej listy VAT MF (kontrahent). */
  whitelist: boolean;
  testIdPrefix: string;
  t: TranslateFn;
}

export function buildBankAccountColumns({
  listColumns,
  accounts,
  whitelist,
  testIdPrefix,
  t,
}: BuildBankAccountColumnsArgs): ColumnDefinition<BankAccountRow>[] {
  const cols = listColumns.map((field) => buildFieldColumn(field, accounts, testIdPrefix, t));
  if (whitelist) cols.push(buildWhitelistColumn(testIdPrefix, t));
  return cols;
}
